import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart, FaMapMarkerAlt } from "react-icons/fa";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";

export default function TopRatedHotels({ limit }) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wishlist, setWishlist] = useState([]);
    const navigate = useNavigate();

    // Load wishlist from localStorage
    useEffect(() => {
        const storedWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
        setWishlist(storedWishlist);
    }, []);

    // Save wishlist
    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };

    // Toggle wishlist
    const toggleWishlist = (hotel) => {
        const isLoggedIn = true; // replace later with auth

        if (!isLoggedIn) {
            alert("Please login to add to wishlist");
            return;
        }

        const exists = wishlist.find(item => item.id === hotel.id);

        let updated;
        if (exists) {
            updated = wishlist.filter(item => item.id !== hotel.id);
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    const isWishlisted = (id) => {
        return wishlist.some(item => item.id === id);
    };

    useEffect(() => {
        const fetchTopHotels = async () => {
            try {
                const res = await axios.get("https://jumeiradiscounthotels.com/api/top-rated-hotels");
                setHotels(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTopHotels();
    }, []);

    // ⭐ render stars
    const renderStars = (rating) => {
        const stars = [];
        const safeRating = rating || 0;

        for (let i = 0; i < 5; i++) {
            if (i < Math.floor(safeRating)) {
                stars.push(<FaStar key={i} />);
            } else if (i === Math.floor(safeRating) && safeRating % 1 >= 0.5) {
                stars.push(<FaStarHalfAlt key={i} />);
            } else {
                stars.push(<FaRegStar key={i} className="text-gray-300" />);
            }
        }

        return stars;
    };

    return (
        <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">

                    <div>
                        <span className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 text-[#0F172A] text-sm font-semibold mb-4">
                            Top Rated Hotels
                        </span>

                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                            Luxury Stays Recommended For You
                        </h2>

                        <p className="text-slate-500 mt-3 max-w-2xl">
                            Handpicked hotels with exceptional ratings, premium amenities and
                            unbeatable locations across Dubai.
                        </p>
                    </div>

                    <button
                        onClick={() => navigate("/hotels")}
                        className="hidden sm:flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#002B45] via-[#174B5F] to-[#D4A83F] hover:bg-gradient-to-br text-white rounded-xl  transition"
                    >
                        View All Hotels →
                    </button>

                </div>

                {/* Loading */}
                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin h-10 w-10 border-4 border-teal-500 border-t-transparent rounded-full"></div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
                            {hotels.slice(0, limit).map((hotel) => (
                                <div
                                    key={hotel.id}
                                    className="group relative bg-white rounded-3xl overflow-hidden border border-slate-200/80 shadow-[0_8px_30px_rgba(15,23,42,0.08)] hover:shadow-[0_20px_50px_rgba(15,23,42,0.16)] hover:-translate-y-1.5 transition-all duration-500"
                                >
                                    {/* IMAGE SECTION */}
                                    <div
                                        className="relative h-[255px] sm:h-[270px] overflow-hidden cursor-pointer"
                                        onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                    >
                                        <img
                                            src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                            alt={hotel.name}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />

                                        {/* Elegant Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#001925]/90 via-[#001925]/10 to-transparent"></div>

                                        {/* Top Left - Rating */}
                                        <div className="absolute top-4 left-4">
                                            <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur-md px-3 py-1.5 rounded-full shadow-lg">
                                                <div className="flex items-center gap-0.5">
                                                    {renderStars(hotel.rating)}
                                                </div>

                                                <span className="text-slate-700 text-xs font-bold">
                                                    {hotel.rating}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Wishlist */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleWishlist(hotel);
                                            }}
                                            className="absolute top-4 right-4 w-11 h-11 bg-white/95 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg hover:scale-110 hover:bg-white transition-all duration-300"
                                        >
                                            {isWishlisted(hotel.id) ? (
                                                <FaHeart className="text-red-500 text-base" />
                                            ) : (
                                                <FaRegHeart className="text-slate-600 text-base" />
                                            )}
                                        </button>

                                        {/* Bottom Image Content */}
                                        <div className="absolute bottom-0 left-0 right-0 p-5">

                                            {/* Location */}
                                            <div className="flex items-center gap-2 text-white/90 mb-2">
                                                <FaMapMarkerAlt className="text-[#D4A83F] text-sm flex-shrink-0" />

                                                <span className="text-xs sm:text-sm font-medium line-clamp-1">
                                                    {hotel.location}
                                                </span>
                                            </div>

                                            {/* Hotel Name */}
                                            <h3
                                                onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                                className="text-xl sm:text-2xl font-bold text-white leading-tight line-clamp-1 cursor-pointer hover:text-[#D4A83F] transition-colors duration-300"
                                            >
                                                {hotel.name}
                                            </h3>
                                        </div>
                                    </div>

                                    {/* CONTENT */}
                                    <div className="p-5 sm:p-6">

                                        {/* Description */}
                                        <p className="text-slate-500 text-sm leading-6 line-clamp-2 min-h-[48px]">
                                            {hotel.short_description?.replace(/<[^>]*>/g, "")}
                                        </p>

                                        {/* Divider */}
                                        <div className="border-t border-slate-100 my-5"></div>

                                        {/* Bottom Info */}
                                        <div className="flex items-end justify-between gap-4">

                                            {/* Price */}
                                            <div>
                                                <span className="block text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
                                                    Starting from
                                                </span>

                                                <div className="flex items-baseline gap-1">
                                                    <span className="text-2xl font-extrabold text-[#002B45]">
                                                        {Math.round(hotel.price_per_night)}
                                                    </span>

                                                    <span className="text-sm font-semibold text-slate-500">
                                                        AED
                                                    </span>
                                                </div>

                                                <span className="text-xs text-slate-400">
                                                    per night
                                                </span>
                                            </div>

                                            {/* View Hotel Button */}
                                            <button
                                                onClick={() =>
                                                    navigate(`/hotels/${hotel.slug}`)
                                                }
                                                className="group/btn flex items-center gap-2 bg-[#002B45] hover:bg-[#174B5F] text-white px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 shadow-md hover:shadow-lg"
                                            >
                                                View Hotel

                                                <span className="text-lg leading-none group-hover/btn:translate-x-1 transition-transform duration-300">
                                                    →
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Mobile Button */}
                        <div className="mt-10 sm:hidden">
                            <button
                                onClick={() => navigate("/hotels")}
                                className="w-full bg-gradient-to-r from-[#002B45] via-[#174B5F] to-[#D4A83F] hover:bg-gradient-to-br text-white py-3.5 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                            >
                                View All Hotels
                            </button>
                        </div>
                    </>
                )}
            </div >

        </section >
    );
}